export const primaryAuditCompliance = summary => {
  const active = summary?.active_bep_compliance;
  if (active) return { ...active, label: 'applicable active BEP results' };
  const assessed = Math.max(0, (summary?.total_elements || 0) - (summary?.not_assessed_count || 0));
  return {
    pass: summary?.pass_count || 0,
    fail: summary?.fail_count || 0,
    assessed,
    pass_rate: summary?.pass_rate || 0,
    label: 'assessed elements',
  };
};

const isFiniteNumber = (value) => typeof value === 'number' && Number.isFinite(value);

const nonNegativeCount = value => (
  isFiniteNumber(value) && value >= 0 ? value : null
);

/**
 * Normalize one-count-per-element outcomes across modern and historical audits.
 * Legacy top-level pass/fail counts are deliberately excluded because newer
 * audit schemas use them for applicable rule checks rather than unique elements.
 */
export const resolveElementStatusCounts = (summary, totalElements) => {
  const modern = summary?.element_compliance || {};
  const historical = summary?.chart_aggregates?.status_counts || {};
  const total = nonNegativeCount(totalElements);
  const counts = {
    PASS: nonNegativeCount(modern.pass) ?? nonNegativeCount(historical.PASS),
    WARN: nonNegativeCount(historical.WARN) ?? nonNegativeCount(summary?.warn_count),
    REVIEW: nonNegativeCount(historical.REVIEW) ?? nonNegativeCount(summary?.review_count),
    FAIL: nonNegativeCount(modern.fail) ?? nonNegativeCount(historical.FAIL),
    NOT_ASSESSED: nonNegativeCount(modern.not_assessed) ?? nonNegativeCount(summary?.not_assessed_count),
  };
  const values = Object.values(counts);
  const source = nonNegativeCount(modern.pass) !== null
    ? 'element_compliance'
    : nonNegativeCount(historical.PASS) !== null
      ? 'chart_aggregates.status_counts'
      : null;
  if (total === null || values.some(value => value === null)) {
    return { available: false, counts: null, source: null, reason: 'missing' };
  }
  if (values.reduce((sum, value) => sum + value, 0) !== total) {
    return { available: false, counts: null, source: null, reason: 'non_conserving' };
  }
  return { available: true, counts, source, reason: null };
};

const usableMetric = (metric, passKey = 'pass', failKey = 'fail') => (
  metric
  && nonNegativeCount(metric.assessed) !== null
  && (metric.assessed === 0 || (
    nonNegativeCount(metric[passKey]) !== null
    && nonNegativeCount(metric[failKey]) !== null
  ))
);

export const resolveApplicableCheckCompliance = (summary) => {
  if (usableMetric(summary?.active_bep_compliance)) {
    return { metric: summary.active_bep_compliance, source: 'active_bep_compliance' };
  }
  const legacy = summary?.category_breakdown?.custom_bep_rules;
  const pass = nonNegativeCount(legacy?.pass);
  const fail = nonNegativeCount(legacy?.fail);
  const warn = nonNegativeCount(legacy?.warn);
  const total = nonNegativeCount(legacy?.total);
  const declaredTotals = [summary?.applicable_checks_count, summary?.custom_bep_rows_persisted]
    .map(nonNegativeCount)
    .filter(value => value !== null);
  if ([pass, fail, warn, total].some(value => value === null)
      || pass + fail + warn !== total
      || declaredTotals.some(value => value !== total)) {
    return { metric: null, source: null };
  }
  return {
    metric: {
      assessed: total,
      pass,
      fail,
      warn,
      skipped: 0,
      pass_rate: total > 0 ? (pass / total) * 100 : null,
    },
    source: 'category_breakdown.custom_bep_rules',
  };
};

export const resolveElementComplianceMetric = (summary, totalElements) => {
  if (usableMetric(summary?.element_compliance)) {
    return { metric: summary.element_compliance, source: 'element_compliance' };
  }
  const resolved = resolveElementStatusCounts(summary, totalElements);
  if (!resolved.available) return { metric: null, source: null };
  const { PASS, WARN, REVIEW, FAIL, NOT_ASSESSED } = resolved.counts;
  const assessed = totalElements - NOT_ASSESSED;
  return {
    metric: {
      assessed,
      pass: PASS,
      fail: WARN + REVIEW + FAIL,
      not_assessed: NOT_ASSESSED,
      pass_rate: assessed > 0 ? (PASS / assessed) * 100 : null,
    },
    source: resolved.source,
  };
};

export const resolveModelPreflightCompliance = (summary) => {
  if (usableMetric(summary?.model_preflight_compliance, 'passed', 'failed')) {
    return { metric: summary.model_preflight_compliance, source: 'model_preflight_compliance' };
  }
  const checks = summary?.model_level_checks;
  if (!Array.isArray(checks) || checks.length === 0) return { metric: null, source: null };
  const passStatuses = new Set(['PASS', 'PASSED']);
  const failStatuses = new Set(['FAIL', 'FAILED', 'ERROR']);
  const skipStatuses = new Set(['SKIPPED', 'NOT_APPLICABLE', 'N/A']);
  const statuses = checks.map(check => String(check?.status || '').toUpperCase());
  if (statuses.some(status => !passStatuses.has(status) && !failStatuses.has(status) && !skipStatuses.has(status))) {
    return { metric: null, source: null };
  }
  const passed = statuses.filter(status => passStatuses.has(status)).length;
  const failed = statuses.filter(status => failStatuses.has(status)).length;
  const skipped = statuses.filter(status => skipStatuses.has(status)).length;
  const assessed = passed + failed;
  return {
    metric: {
      assessed,
      passed,
      failed,
      skipped,
      pass_rate: assessed > 0 ? (passed / assessed) * 100 : null,
    },
    source: 'model_level_checks',
  };
};

export const resolveActiveRuleEvidence = (summary, auditActiveRuleCount) => {
  const snapshot = summary?.active_confirmed_rule_snapshot;
  const snapshotCount = Array.isArray(snapshot)
    ? snapshot.length
    : Array.isArray(snapshot?.rules)
      ? snapshot.rules.length
      : Array.isArray(snapshot?.confirmed_rule_ids)
        ? snapshot.confirmed_rule_ids.length
        : nonNegativeCount(snapshot?.count);
  if (snapshotCount !== null) {
    return { available: true, count: snapshotCount, immutableSnapshot: true };
  }
  const historicalCount = nonNegativeCount(auditActiveRuleCount)
    ?? nonNegativeCount(summary?.active_rule_count);
  return historicalCount === null
    ? { available: false, count: null, immutableSnapshot: false }
    : { available: true, count: historicalCount, immutableSnapshot: false };
};

/**
 * Resolves the persisted element-rule check count for Custom BEP rules only.
 * This deliberately excludes skipped pairs and model/file preflight checks.
 */
export const applicableElementChecks = (summary) => {
  const customBep = summary?.category_breakdown?.custom_bep_rules;
  if (isFiniteNumber(customBep?.total)) {
    return { available: true, count: customBep.total, source: 'category_breakdown.total' };
  }
  if (isFiniteNumber(summary?.custom_bep_rows_persisted)) {
    return { available: true, count: summary.custom_bep_rows_persisted, source: 'custom_bep_rows_persisted' };
  }
  if (customBep) {
    const pass = customBep.pass ?? customBep.passed;
    const fail = customBep.fail ?? customBep.failures;
    const warn = customBep.warn ?? customBep.warnings;
    if (isFiniteNumber(pass) && isFiniteNumber(fail) && isFiniteNumber(warn)) {
      return { available: true, count: pass + fail + warn, source: 'category_breakdown.statuses' };
    }
  }
  return { available: false, count: null, source: null };
};